import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { toast } from "react-toastify";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import { userSelector } from "../../../../slices/user/userSlice";
import PickupRequestsService from "../../../../app/data/pickup-requests/pickupRequestsService";
import { PickupStatsQuery } from "../../../../app/data/pickup-requests/models";
import Loading from "../../../../ui-components/loading/loading";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import ChartCard from "../../../shipment-reconciliation/dashboard/chart-card/chartCard";
import { ReactComponent as ChartIcon } from "../../../../images/chart.svg";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "../utils/pickupInboxQueryOptions";
import {
  formatCount,
  formatPercent,
  getReviewReasonLabel,
} from "../utils/pickupStatsFormatters";
import {
  buildPickupVolumeChartData,
  getVolumeChartTitle,
  PickupTimeSeriesViewMode,
} from "../utils/pickupTimeSeriesChartUtils";
import PickupInboxAnalyticsSummary from "./pickupInboxAnalyticsSummary";
import "./pickupInboxAnalytics.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const pickupRequestsService = PickupRequestsService.getInstance();

const defaultDateFrom = () => moment().subtract(30, "days").toString();
const defaultDateTo = () => moment().toString();

const toStatsQuery = (dateFrom: string, dateTo: string): PickupStatsQuery => ({
  from: moment(dateFrom).startOf("day").toISOString(),
  to: moment(dateTo).endOf("day").toISOString(),
});

const readDatesFromSearch = (search: string) => {
  const q = new URLSearchParams(search);
  const from = q.get("from");
  const to = q.get("to");
  return {
    dateFrom: from ? moment(from).toString() : defaultDateFrom(),
    dateTo: to ? moment(to).toString() : defaultDateTo(),
  };
};

const PickupInboxAnalyticsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const userState = useSelector(userSelector);

  const terminalPrefix = userState.activeTerminal?.id
    ? `/${userState.activeTerminal.id}`
    : "";

  const [dateFrom, setDateFrom] = useState(
    () => readDatesFromSearch(location.search).dateFrom
  );
  const [dateTo, setDateTo] = useState(() => readDatesFromSearch(location.search).dateTo);
  const [volumeViewMode, setVolumeViewMode] = useState<PickupTimeSeriesViewMode>("daily");

  const statsQuery = useMemo(() => toStatsQuery(dateFrom, dateTo), [dateFrom, dateTo]);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "pickup_inbox_analytics" });
  }, []);

  const syncDateUrl = useCallback(
    (from: string, to: string) => {
      const q = new URLSearchParams(location.search);
      q.set("from", moment(from).format("YYYY-MM-DD"));
      q.set("to", moment(to).format("YYYY-MM-DD"));
      history.replace({
        pathname: `${terminalPrefix}${Routes.pickup.inboxAnalytics}`,
        search: `?${q.toString()}`,
      });
    },
    [history, location.search, terminalPrefix]
  );

  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    syncDateUrl(dateFrom, dateTo);
  }, [dateFrom, dateTo, syncDateUrl]);

  const queryKeySuffix = [statsQuery.from, statsQuery.to];

  const {
    data: analytics,
    isLoading: analyticsLoading,
    isError: analyticsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_STATS_ANALYTICS, ...queryKeySuffix],
    queryFn: () => pickupRequestsService.getStatsAnalytics(statsQuery),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_STATS_SUMMARY, ...queryKeySuffix],
    queryFn: () => pickupRequestsService.getStatsSummary(statsQuery),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const isLoading = analyticsLoading || summaryLoading;

  useEffect(() => {
    if (analyticsError || summaryError) {
      toast.error("Unable to load analytics. Try again or contact support.");
    }
  }, [analyticsError, summaryError]);

  const timeSeriesChart = useMemo(() => {
    const series = analytics?.timeSeries ?? [];
    if (!series.length) return null;
    return buildPickupVolumeChartData(series, volumeViewMode);
  }, [analytics?.timeSeries, volumeViewMode]);

  const reasonBreakdownChart = useMemo(() => {
    const breakdown = analytics?.reviewReasonBreakdown ?? [];
    if (!breakdown.length) return null;

    const sorted = [...breakdown].sort((a, b) => b.count - a.count);
    const total = sorted.reduce((sum, item) => sum + item.count, 0);

    return {
      labels: sorted.map(getReviewReasonLabel),
      datasets: [
        {
          label: "Count",
          data: sorted.map((item) => item.count),
          backgroundColor: "#2F80ED",
        },
      ],
      total,
      sorted,
    };
  }, [analytics?.reviewReasonBreakdown]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      title: {
        display: true,
        text: getVolumeChartTitle(volumeViewMode),
      },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  const barOptions = {
    ...chartOptions,
    indexAxis: "y" as const,
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false },
    },
  };

  const inboxLink = `${terminalPrefix}${Routes.pickup.inbox}${location.search}`;

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.PICKUP_INBOX_ANALYTICS}
          title="Pickup Inbox Analytics"
          mods={{ "full-width": true }}
        />
      }
    >
      <div className="xgs-pickup-inbox-analytics">
        <div className="xgs-pickup-inbox-analytics__controls">
          <Link to={inboxLink} className="xgs-pickup-inbox-analytics__back-link">
            ← Back to inbox
          </Link>
          <LabeledDateRangeInput
            label="Date range"
            labelMode={LabelModes.column}
            className="xgs-pickup-inbox-analytics__date-range"
            start={dateFrom}
            end={dateTo}
            onStartChange={setDateFrom}
            onEndChange={setDateTo}
          />
        </div>

        <PickupInboxAnalyticsSummary
          summary={summary}
          analytics={analytics}
          isLoading={isLoading}
        />

        <ChartCard title="Volume over time" icon={<ChartIcon />}>
          {isLoading ? (
            <div className="xgs-pickup-inbox-analytics__chart-loading">
              <Loading isLoading />
            </div>
          ) : timeSeriesChart ? (
            <>
              <div className="xgs-pickup-inbox-analytics__chart-view-modes">
                <Button
                  theme={ButtonThemes.blue}
                  size={ButtonSizes.autoMedium}
                  onClick={() => setVolumeViewMode("daily")}
                  disabled={volumeViewMode === "daily"}
                >
                  Daily
                </Button>
                <Button
                  theme={ButtonThemes.blue}
                  size={ButtonSizes.autoMedium}
                  onClick={() => setVolumeViewMode("weekly")}
                  disabled={volumeViewMode === "weekly"}
                >
                  Weekly
                </Button>
                <Button
                  theme={ButtonThemes.blue}
                  size={ButtonSizes.autoMedium}
                  onClick={() => setVolumeViewMode("monthly")}
                  disabled={volumeViewMode === "monthly"}
                >
                  Monthly
                </Button>
              </div>
              <div className="xgs-pickup-inbox-analytics__chart">
                <Line data={timeSeriesChart} options={chartOptions} />
              </div>
            </>
          ) : (
            <p className="xgs-pickup-inbox-analytics__empty">
              No volume data in this period.
            </p>
          )}
        </ChartCard>

        <ChartCard title="Review reason breakdown" icon={<ChartIcon />}>
          {isLoading ? (
            <div className="xgs-pickup-inbox-analytics__chart-loading">
              <Loading isLoading />
            </div>
          ) : reasonBreakdownChart ? (
            <>
              <div className="xgs-pickup-inbox-analytics__chart xgs-pickup-inbox-analytics__chart--bar">
                <Bar
                  data={{
                    labels: reasonBreakdownChart.labels,
                    datasets: reasonBreakdownChart.datasets,
                  }}
                  options={barOptions}
                />
              </div>
              <table className="xgs-pickup-inbox-analytics__breakdown-table">
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th>Count</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {reasonBreakdownChart.sorted.map((item, index) => {
                    const label = getReviewReasonLabel(item);
                    return (
                      <tr key={`${label}-${index}`}>
                        <td>{label}</td>
                        <td>{formatCount(item.count)}</td>
                        <td>
                          {reasonBreakdownChart.total
                            ? formatPercent(item.count / reasonBreakdownChart.total)
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <p className="xgs-pickup-inbox-analytics__empty">
              No review reasons in this period.
            </p>
          )}
        </ChartCard>
      </div>
    </ContentContainer>
  );
};

export default PickupInboxAnalyticsPage;
